import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import {
  ArrowLeft, Trash, Users, FileText, ChartBar, Phone, Envelope, Buildings,
  UserCircle, ArrowsClockwise, Heartbeat, CheckCircle, XCircle, Spinner, PencilSimple, Crown,
  Lock, ShieldCheck, Eye, EyeSlash, CurrencyDollar, Receipt, Prohibit,
  Newspaper, Lightning, Calendar, ChartLine, Globe, Link as LinkIcon, DownloadSimple,
  IdentificationCard, MapPin, Clock,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const STATUS_META = {
  draft:      { label: "Draft",      color: "#94A3B8", icon: PencilSimple,    bg: "rgba(148,163,184,0.12)" },
  generating: { label: "Generating", color: "#3B82F6", icon: Spinner,         bg: "rgba(59,130,246,0.12)"  },
  generated:  { label: "Generated",  color: "#01AF9B", icon: CheckCircle,     bg: "rgba(1,175,155,0.12)"   },
  error:      { label: "Error",      color: "#EF4444", icon: XCircle,         bg: "rgba(239,68,68,0.12)"   },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: meta.bg, color: meta.color }}>
      <Icon size={12} weight={status === "generating" ? "bold" : "fill"} className={status === "generating" ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
};

const HealthKPI = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30`, color }}>
        <Icon size={18} weight="bold" />
      </div>
      <div className="flex flex-col">
        <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">{label}</span>
        {subtext && <span className="text-slate-500 text-[10px]">{subtext}</span>}
      </div>
    </div>
    <div className="font-['Outfit'] text-3xl font-bold text-white" style={{ color: (value || 0) > 0 && color === "#EF4444" ? "#EF4444" : "#FFFFFF" }}>{value ?? 0}</div>
  </div>
);

const GenerationHealthPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminGenerationHealth();
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const handleRetry = async (id) => {
    if (!window.confirm("Re-trigger AI generation for this report? (consumes API credits)")) return;
    setRetrying(id);
    try {
      await api.adminRetryReport(id);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Retry failed"); }
    setRetrying(null);
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data) return null;

  const sc = data.status_counts || {};
  const h24 = data.last_24h || {};
  const reports = data.recent_reports || [];

  return (
    <div className="space-y-6" data-testid="health-panel">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <HealthKPI icon={CheckCircle} label="Generated" value={sc.generated} color="#01AF9B" />
        <HealthKPI icon={Spinner}     label="In Progress" value={sc.generating} color="#3B82F6" />
        <HealthKPI icon={XCircle}     label="Errors (total)" value={sc.error} color="#EF4444" />
        <HealthKPI icon={PencilSimple} label="Drafts"    value={sc.draft} color="#94A3B8" />
        <HealthKPI icon={CheckCircle} label="Generated" value={h24.generated} color="#01AF9B" subtext="Last 24h" />
        <HealthKPI icon={XCircle}     label="Errors"    value={h24.errors} color="#EF4444" subtext="Last 24h" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">Recent Reports ({reports.length})</h3>
        <button onClick={load} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="health-refresh">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Admin preview info banner, renders any report through the current
          production pdf_generator (V3 merged layout). Skips ownership check. */}
      {reports.some(r => r.status === "generated") && (
        <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 to-purple-500/5 p-4 flex items-start gap-3" data-testid="v2-preview-banner">
          <FileText size={20} weight="fill" className="text-emerald-300 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-['Outfit'] text-sm font-bold text-emerald-100">Admin Report Download</div>
            <div className="text-slate-300 text-xs mt-1 leading-relaxed">
              <span className="font-semibold text-emerald-200">Download Report</span> renders any user&apos;s PDF through the current production pipeline (identical to what the user receives). Bypasses the ownership check for support purposes.
            </div>
          </div>
        </div>
      )}

      {/* Reports table */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-800/60">
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">Status</th>
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">Company</th>
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">User</th>
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">Lang</th>
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">Last Update</th>
                <th className="px-4 py-3 font-['Outfit'] text-[10px] font-bold tracking-wider uppercase text-slate-500">Issue / Action</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 text-sm">No reports yet.</td></tr>
              )}
              {reports.map(r => {
                const isError = r.status === "error";
                const expanded = expandedId === r._id;
                return (
                  <React.Fragment key={r._id}>
                    <tr className="border-b border-slate-800/30 hover:bg-[#0B0C10] transition-colors" data-testid={`health-row-${r._id}`}>
                      <td className="px-4 py-3"><StatusPill status={r.status || "draft"} /></td>
                      <td className="px-4 py-3 text-slate-200 text-sm font-medium">{r.company_name || "-"}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{r.user_email || "-"}</td>
                      <td className="px-4 py-3"><span className="uppercase text-[10px] font-bold text-slate-500">{r.language || "en"}</span></td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* v2 preview: renders through pdf_generator_v2 + hammer.
                              Only shown for generated reports. */}
                          {r.status === "generated" && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${r._id}/pdf-preview-v2?lang=${r.language || "en"}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                // Attach admin token via a fetch-then-blob because
                                // <a> can't send Authorization headers directly.
                                // Trigger a real download (Content-Disposition) rather
                                // than window.open, browsers block popups launched
                                // from an async .then() callback after a fetch.
                                e.preventDefault();
                                const token = localStorage.getItem("esg_token");
                                const btn = e.currentTarget;
                                const orig = btn.innerHTML;
                                btn.style.pointerEvents = "none";
                                btn.innerHTML = '<span class="inline-block w-3 h-3 rounded-full border-2 border-emerald-200 border-t-transparent animate-spin"></span> Generating…';
                                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${r._id}/pdf-preview-v2?lang=${r.language || "en"}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                })
                                  .then(async res => {
                                    if (res.status === 202) throw new Error("The PDF is being prepared on the server. Please click Download Report again in about a minute.");
                                    if (!res.ok) throw new Error(res.status === 524 ? "The server is still building this PDF. Please click Download Report again in about a minute." : `HTTP ${res.status}: ${(await res.text()).slice(0, 200)}`);
                                    return res.blob();
                                  })
                                  .then(blob => {
                                    const url = URL.createObjectURL(blob);
                                    // Trigger a real download via a synthetic anchor.
                                    // Fallback: open in new tab if download attr is blocked.
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `${(r.company_name || "report").replace(/\s+/g, "_")}_ESG_admin_preview.pdf`;
                                    a.rel = "noopener noreferrer";
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  })
                                  .catch(err => alert(err.message))
                                  .finally(() => {
                                    btn.style.pointerEvents = "";
                                    btn.innerHTML = orig;
                                  });
                              }}
                              className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg bg-emerald-500/20 border-2 border-emerald-400 text-emerald-100 hover:bg-emerald-500/30 hover:border-emerald-300 shadow-lg shadow-emerald-500/20 transition-all"
                              data-testid={`admin-pdf-preview-${r._id}`}
                              title="Download this user's report PDF. Bypasses ownership check for support purposes."
                            >
                              <FileText size={14} weight="fill" /> Download Report
                            </a>
                          )}
                        {isError ? (
                          <>
                            <button onClick={() => setExpandedId(expanded ? null : r._id)} className="text-xs text-slate-400 hover:text-white underline" data-testid={`health-details-${r._id}`}>
                              {expanded ? "Hide" : "Details"}
                            </button>
                            <button onClick={() => handleRetry(r._id)} disabled={retrying === r._id}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-[#01AF9B]/40 text-[#01AF9B] hover:bg-[#01AF9B]/10 transition-colors disabled:opacity-40"
                              data-testid={`health-retry-${r._id}`}>
                              <ArrowsClockwise size={12} className={retrying === r._id ? "animate-spin" : ""} />
                              {retrying === r._id ? "Retrying…" : "Retry"}
                            </button>
                          </>
                        ) : (
                          !r.status || r.status === "generated" ? null : <span className="text-xs text-slate-600">-</span>
                        )}
                        </div>
                      </td>
                    </tr>
                    {expanded && isError && (
                      <tr className="bg-[#0B0C10]">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="text-xs font-semibold text-red-400 mb-1.5">User-facing message</div>
                          <div className="text-sm text-slate-200 mb-3">{r.error_message || "-"}</div>
                          {r.error_detail && (
                            <>
                              <div className="text-xs font-semibold text-slate-500 mb-1.5">Technical detail (not shown to user)</div>
                              <pre className="text-[11px] text-slate-500 whitespace-pre-wrap font-mono bg-[#050506] rounded-lg p-3 border border-slate-800/60 max-w-full overflow-x-auto">{r.error_detail}</pre>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const UsersPanel = ({ users, setUsers, setStats }) => {
  const { user: me } = useAuth();
  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user ${email} and all their data? This cannot be undone.`)) return;
    try {
      await api.adminDeleteUser(userId);
      setUsers(u => u.filter(x => x._id !== userId));
      setStats(s => ({ ...s, total_users: s.total_users - 1 }));
    } catch (err) { alert(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleToggleGrandfather = async (userId, email, current) => {
    const next = !current;
    const verb = next ? "Grant lifetime free access" : "REVOKE lifetime free access";
    if (!window.confirm(`${verb} for ${email}?`)) return;
    try {
      await api.adminGrandfather(userId, next);
      setUsers(u => u.map(x => x._id === userId ? { ...x, grandfathered: next } : x));
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  // Split users: subscribers = admin + grandfathered + active paid subscription.
  // Free users = everyone else (fresh signups + expired/cancelled).
  // When a free user pays, their subscription.status flips to "active" via the
  // Paddle webhook and they automatically move from the Free table to the
  // Subscribers table on next refresh.
  const isSubscriber = (u) =>
    u.role === "admin" ||
    u.grandfathered === true ||
    (u.subscription && ["active", "trialing", "ACTIVE"].includes(u.subscription.status));

  const subscribers = users.filter(isSubscriber);
  const freeUsers   = users.filter(u => !isSubscriber(u));

  const planBadge = (u) => {
    const isGrandfathered = u.grandfathered === true;
    if (u.role === "admin")
      return <span className="text-[10px] font-bold uppercase tracking-wider text-[#F97316]">Admin</span>;
    if (isGrandfathered)
      return <span className="text-[10px] font-bold uppercase tracking-wider text-[#01AF9B]">Lifetime free</span>;
    if (u.subscription?.status === "active")
      return <span className="text-[10px] font-bold uppercase tracking-wider text-[#01AF9B]">Active</span>;
    return <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Free</span>;
  };

  const rowActions = (u) => {
    const isMe = me && (u._id === me.id || u.email === me.email);
    const isGrandfathered = u.grandfathered === true;
    return (
      <div className="flex items-center gap-2">
        {u.role !== "admin" && (
          <button onClick={() => handleToggleGrandfather(u._id, u.email, isGrandfathered)}
            title={isGrandfathered ? "Revoke lifetime access" : "Grant lifetime access"}
            className={`p-1 transition-colors ${isGrandfathered ? "text-[#01AF9B] hover:text-[#F97316]" : "text-slate-600 hover:text-[#01AF9B]"}`}
            data-testid={`grandfather-${u._id}`}>
            <Crown size={16} weight={isGrandfathered ? "fill" : "regular"} />
          </button>
        )}
        {!isMe && (
          <button onClick={() => handleDelete(u._id, u.email)}
            title="Delete user"
            className="text-slate-600 hover:text-red-400 transition-colors p-1"
            data-testid={`delete-user-${u._id}`}>
            <Trash size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ─── SUBSCRIBERS (full data) ─── */}
      <div className="bg-[#111216] border border-[#01AF9B]/30 rounded-xl overflow-hidden" data-testid="admin-subscribers-table">
        <div className="px-6 py-4 border-b border-slate-800/60 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-lg font-bold text-white flex items-center gap-2">
            <Crown size={18} weight="fill" className="text-[#01AF9B]" />
            Subscribers ({subscribers.length})
          </h2>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Paid / grandfathered / admin</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-800/60">
                {["Company", "Name", "Role", "Email", "Phone", "Reports", "Plan", "Joined", "Actions"].map((h, i) => (
                  <th key={i} className="px-5 py-3 font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subscribers.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-8 text-center text-slate-500 text-sm">No subscribers yet. Free users who complete a Paddle payment will appear here automatically.</td></tr>
              ) : subscribers.map(u => (
                <tr key={u._id} className="border-b border-slate-800/30 hover:bg-[#0B0C10] transition-colors" data-testid={`subscriber-row-${u._id}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><Buildings size={14} className="text-slate-500" /><span className="text-slate-200 text-sm font-medium">{u.company_name || "-"}</span></div></td>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><UserCircle size={14} className="text-slate-500" /><span className="text-slate-200 text-sm">{u.name || ""} {u.surname || ""}</span></div></td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{u.role_title || u.role || "-"}</td>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><Envelope size={14} className="text-slate-500" /><span className="text-slate-300 text-sm">{u.email}</span></div></td>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><Phone size={14} className="text-slate-500" /><span className="text-slate-400 text-sm">{u.phone || "-"}</span></div></td>
                  <td className="px-5 py-3"><span className="bg-[#01AF9B]/10 text-[#01AF9B] text-xs font-semibold px-2 py-1 rounded-full">{u.report_count || 0}</span></td>
                  <td className="px-5 py-3">{planBadge(u)}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                  <td className="px-5 py-3">{rowActions(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── FREE USERS (minimal data) ─── */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl overflow-hidden" data-testid="admin-free-users-table">
        <div className="px-6 py-4 border-b border-slate-800/60 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-lg font-bold text-white">Free Users ({freeUsers.length})</h2>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Signed up, not yet paid</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-800/60">
                {["Name", "Surname", "Email", "Reports", "Joined", "Actions"].map((h, i) => (
                  <th key={i} className="px-5 py-3 font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {freeUsers.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-sm">No free users right now.</td></tr>
              ) : freeUsers.map(u => (
                <tr key={u._id} className="border-b border-slate-800/30 hover:bg-[#0B0C10] transition-colors" data-testid={`free-user-row-${u._id}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><UserCircle size={14} className="text-slate-500" /><span className="text-slate-200 text-sm">{u.name || "-"}</span></div></td>
                  <td className="px-5 py-3 text-slate-200 text-sm">{u.surname || "-"}</td>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><Envelope size={14} className="text-slate-500" /><span className="text-slate-300 text-sm">{u.email}</span></div></td>
                  <td className="px-5 py-3"><span className="bg-slate-700/40 text-slate-400 text-xs font-semibold px-2 py-1 rounded-full">{u.report_count || 0}</span></td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                  <td className="px-5 py-3">{rowActions(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'success' | 'error', text }

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!current || !next) return setMsg({ type: "error", text: "Please fill in both passwords." });
    if (next.length < 8) return setMsg({ type: "error", text: "New password must be at least 8 characters." });
    if (next !== confirm) return setMsg({ type: "error", text: "New password and confirmation do not match." });
    if (next === current) return setMsg({ type: "error", text: "New password must be different from the current one." });
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setMsg({ type: "success", text: "Password changed successfully. Use the new password next time you log in." });
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to change password.";
      setMsg({ type: "error", text: typeof detail === "string" ? detail : "Failed to change password." });
    }
    setBusy(false);
  };

  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#01AF9B15", border: "1px solid #01AF9B30", color: "#01AF9B" }}>
          <Lock size={18} weight="bold" />
        </div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-white">Change Admin Password</h3>
      </div>
      <p className="text-slate-500 text-xs mb-5 ml-12">Use a strong password, at least 8 characters. You will stay logged in after the change.</p>

      <form onSubmit={submit} className="space-y-4 max-w-md">
        <div>
          <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Current password</label>
          <div className="relative">
            <input
              type={showCur ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password"
              className="w-full bg-[#0B0C10] border border-slate-800 rounded-lg px-3 py-2.5 pr-10 text-slate-100 text-sm focus:outline-none focus:border-[#01AF9B] transition-colors"
              data-testid="current-password-input"
            />
            <button type="button" onClick={() => setShowCur(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" tabIndex={-1}>
              {showCur ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1.5 block">New password</label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password"
              className="w-full bg-[#0B0C10] border border-slate-800 rounded-lg px-3 py-2.5 pr-10 text-slate-100 text-sm focus:outline-none focus:border-[#01AF9B] transition-colors"
              data-testid="new-password-input"
            />
            <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" tabIndex={-1}>
              {showNew ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Confirm new password</label>
          <input
            type={showNew ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
            className="w-full bg-[#0B0C10] border border-slate-800 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-[#01AF9B] transition-colors"
            data-testid="confirm-password-input"
          />
        </div>

        {msg && (
          <div className={`text-sm rounded-lg px-3 py-2 ${msg.type === "success" ? "bg-[#01AF9B]/10 text-[#01AF9B] border border-[#01AF9B]/30" : "bg-red-500/10 text-red-400 border border-red-500/30"}`} data-testid="change-password-msg">
            {msg.text}
          </div>
        )}

        <button type="submit" disabled={busy} data-testid="change-password-submit"
          className="bg-[#01AF9B] hover:bg-[#019583] disabled:opacity-50 disabled:cursor-not-allowed text-white font-['Outfit'] font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2">
          {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />}
          {busy ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
};

const LoginHistoryCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminLoginAudit(100, appliedFilter || null);
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [appliedFilter]);

  useEffect(() => { load(); }, [load]);

  const fmtAgent = (ua) => {
    if (!ua) return "-";
    if (/curl/i.test(ua)) return "curl";
    if (/Mobi|Android|iPhone/.test(ua)) return "Mobile browser";
    if (/Chrome\//.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Safari\//.test(ua)) return "Safari";
    if (/Edg\//.test(ua)) return "Edge";
    return ua.slice(0, 30);
  };

  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6" data-testid="login-history-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#73324B15", border: "1px solid #73324B30", color: "#73324B" }}>
            <ShieldCheck size={18} weight="bold" />
          </div>
          <h3 className="font-['Outfit'] text-lg font-semibold text-white">Recent Successful Logins</h3>
        </div>
        <button onClick={load} className="text-slate-500 hover:text-[#01AF9B] transition-colors p-1.5" title="Refresh" data-testid="audit-refresh">
          <ArrowsClockwise size={16} />
        </button>
      </div>
      <p className="text-slate-500 text-xs mb-5 ml-12">Last 100 events across all users. Use the filter to focus on a specific email.</p>

      <div className="flex items-center gap-2 mb-4 max-w-md">
        <input type="text" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") setAppliedFilter(emailFilter.trim()); }}
          placeholder="Filter by email…"
          className="flex-1 bg-[#0B0C10] border border-slate-800 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-[#01AF9B] transition-colors"
          data-testid="audit-email-filter" />
        <button onClick={() => setAppliedFilter(emailFilter.trim())} className="bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm px-3 py-2 rounded-lg transition-colors" data-testid="audit-apply-filter">Apply</button>
        {appliedFilter && (
          <button onClick={() => { setEmailFilter(""); setAppliedFilter(""); }} className="text-slate-500 hover:text-slate-200 text-sm" data-testid="audit-clear-filter">Clear</button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" /></div>
      ) : !data || data.items?.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center">No login events yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-800/60">
                {["When", "Email", "Role", "IP Address", "Browser / Agent"].map((h, i) => (
                  <th key={i} className="px-4 py-2.5 font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-800/30 hover:bg-[#0B0C10] transition-colors" data-testid={`audit-row-${idx}`}>
                  <td className="px-4 py-2.5 text-slate-300 text-sm whitespace-nowrap">{row.timestamp ? new Date(row.timestamp).toLocaleString() : "-"}</td>
                  <td className="px-4 py-2.5 text-slate-200 text-sm">{row.email}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {row.is_admin ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#F97316]">Admin</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">User</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 text-sm font-mono">{row.ip || "-"}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-sm" title={row.user_agent || ""}>{fmtAgent(row.user_agent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SecurityPanel = () => (
  <div className="space-y-6" data-testid="security-panel">
    <ChangePasswordCard />
    <LoginHistoryCard />
  </div>
);


// ─── Sample Downloads ───
// Lead-capture funnel from /sample. Shows every visitor who clicked
// "Download Full Report" (date, time, company, name, surname, email,
// country, IP) — user directive 2026-08-15.
const SampleDownloadsPanel = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.adminSampleDownloads()
      .then(d => setRows(d.items || []))
      .catch(e => setError(e.response?.data?.detail || e.message));
  }, []);
  if (error) return (
    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm" data-testid="sample-downloads-error">{String(error)}</div>
  );
  if (rows === null) return (
    <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" /></div>
  );
  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl overflow-hidden" data-testid="sample-downloads-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
        <div className="flex items-center gap-3">
          <DownloadSimple size={20} className="text-[#01AF9B]" />
          <h3 className="font-['Outfit'] text-lg font-bold text-white">Sample Report Downloads</h3>
        </div>
        <span className="text-xs text-slate-400 font-medium">{rows.length} total</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">No downloads yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sample-downloads-table">
            <thead className="bg-black/30 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Date · Time (UTC)</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Surname</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Country</th>
                <th className="px-4 py-3 text-left">IP</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {rows.map((r, i) => {
                const t = r.timestamp ? new Date(r.timestamp) : null;
                const display = t ? `${t.toISOString().replace('T', ' ').slice(0, 19)}` : '—';
                return (
                  <tr key={i} className="border-t border-slate-800/40 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{display}</td>
                    <td className="px-4 py-3 font-medium">{r.company_name}</td>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3">{r.surname}</td>
                    <td className="px-4 py-3 text-[#01AF9B]"><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></td>
                    <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-xs font-mono">{r.country || '??'}</span></td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{r.ip}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const SUB_STATUS_META = {
  ACTIVE:             { label: "Active",           color: "#01AF9B", bg: "rgba(1,175,155,0.14)" },
  APPROVAL_PENDING:   { label: "Awaiting payment", color: "#F59E0B", bg: "rgba(245,158,11,0.14)" },
  APPROVED:           { label: "Approved",         color: "#3B82F6", bg: "rgba(59,130,246,0.14)" },
  CANCELLED:          { label: "Cancelled",        color: "#94A3B8", bg: "rgba(148,163,184,0.14)" },
  SUSPENDED:          { label: "Suspended",        color: "#F97316", bg: "rgba(249,115,22,0.14)" },
  EXPIRED:            { label: "Expired",          color: "#64748B", bg: "rgba(100,116,139,0.14)" },
  past_due:           { label: "Past due",         color: "#EF4444", bg: "rgba(239,68,68,0.14)" },
};

const SubStatusPill = ({ status, grandfathered }) => {
  if (grandfathered) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ background: "rgba(115,50,75,0.18)", color: "#D9A6BC" }}>
        <Crown size={12} weight="fill" /> Grandfathered
      </span>
    );
  }
  if (!status) {
    return <span className="text-xs text-slate-500">-</span>;
  }
  const meta = SUB_STATUS_META[status] || { label: status, color: "#94A3B8", bg: "rgba(148,163,184,0.14)" };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ background: meta.bg, color: meta.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
};

const SubscribersPanel = ({ stats }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("paying");  // paying | all | grandfathered

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminSubscribers();
      setSubs(d || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (s) => {
    if (!window.confirm(`Cancel ${s.email}'s subscription on PayPal?\n\nThe user keeps access until ${s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "the end of their billing period"}. No refund is issued, use the Refund button for that.`)) return;
    setBusyId(s.id);
    try {
      await api.adminCancelSubscription(s.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Cancel failed. Check the backend logs.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRefund = async (s) => {
    const ok = window.confirm(`Refund the last PayPal charge for ${s.email}?\n\nThis issues a FULL refund of the most recent annual payment via PayPal. Money returns to the customer's card within 10 business days.\n\nContinue?`);
    if (!ok) return;
    setBusyId(s.id);
    try {
      const r = await api.adminRefundSubscription(s.id);
      alert(`Refund issued ✓\nRefund ID: ${r.refund_id}\nAmount: $${r.amount}\nStatus: ${r.status}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Refund failed. Check the backend logs.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = subs.filter(s => {
    if (filter === "all") return true;
    if (filter === "grandfathered") return s.grandfathered;
    // paying = active or past_due (anyone PayPal has on file)
    return !!s.paypal_subscription_id;
  });

  const arr = stats?.arr_usd || 0;
  const activeCount = stats?.active_subscriptions || 0;

  return (
    <div className="space-y-6" data-testid="subscribers-panel">
      {/* Revenue KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-arr">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#3B82F6" }}>
              <ChartBar size={18} weight="bold" />
            </div>
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">ARR</span>
          </div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">${arr.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-1">Annual run-rate</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-active">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "rgba(1,175,155,0.15)", border: "1px solid rgba(1,175,155,0.3)", color: "#01AF9B" }}>
              <CheckCircle size={18} weight="bold" />
            </div>
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Active Subscribers</span>
          </div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">{activeCount}</div>
          <div className="text-xs text-slate-500 mt-1">Paying customers right now</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-plan">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "rgba(115,50,75,0.18)", border: "1px solid rgba(115,50,75,0.3)", color: "#D9A6BC" }}>
              <Receipt size={18} weight="bold" />
            </div>
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Plan</span>
          </div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">${stats?.annual_plan_price_usd || 999}<span className="text-slate-500 text-base font-normal">/yr</span></div>
          <div className="text-xs text-slate-500 mt-1">Annual price (change in Paddle)</div>
        </div>
      </div>

      {/* Filters + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-[#111216] border border-slate-800/60 rounded-lg p-1" data-testid="subscribers-filter">
          {[
            { id: "paying",        label: "Paying" },
            { id: "grandfathered", label: "Grandfathered" },
            { id: "all",           label: "All users" },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`subscribers-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs font-semibold font-['Outfit'] rounded-md transition-all ${
                filter === f.id ? "bg-[#01AF9B] text-white" : "text-slate-400 hover:text-slate-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="subscribers-refresh">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Subscribers table */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl overflow-hidden" data-testid="subscribers-table">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500 text-sm">
            No subscribers in this view yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#0B0C10] border-b border-slate-800/60 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 font-semibold">User</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Renews on</th>
                  <th className="px-4 py-3 font-semibold">PayPal ID</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {filtered.map(s => {
                  const isBusy = busyId === s.id;
                  const canRefund = !!s.paypal_subscription_id && (s.status === "ACTIVE" || s.status === "CANCELLED" || s.status === "EXPIRED" || s.status === "past_due");
                  const canCancel = !!s.paypal_subscription_id && s.status === "ACTIVE" && !s.cancel_at_period_end;
                  return (
                    <tr key={s.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`subscriber-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100">{s.name || "-"}</div>
                        <div className="text-xs text-slate-500">{s.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <SubStatusPill status={s.status} grandfathered={s.grandfathered} />
                        {s.cancel_at_period_end && <div className="text-[10px] text-amber-400 mt-1">Cancels at period end</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">
                        {s.paypal_subscription_id || "-"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleRefund(s)}
                          disabled={!canRefund || isBusy}
                          data-testid={`subscriber-refund-${s.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-[#73324B]/15 text-[#D9A6BC] hover:bg-[#73324B]/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors mr-2"
                          title={canRefund ? "Refund the last charge via PayPal" : "No completed PayPal charge to refund"}>
                          <Receipt size={12} weight="bold" /> Refund
                        </button>
                        <button
                          onClick={() => handleCancel(s)}
                          disabled={!canCancel || isBusy}
                          data-testid={`subscriber-cancel-${s.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title={canCancel ? "Cancel future renewals" : "Subscription already cancelled or inactive"}>
                          <Prohibit size={12} weight="bold" /> Cancel
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── ESG World News, Tavily Credit Usage (admin-only monitoring) ────────────
const NewsUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.adminNewsUsage()
      .then(setData)
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load usage."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="news-usage-loading">
        <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (err) {
    return <div className="bg-red-500/10 border border-red-500/40 text-red-200 rounded-xl p-5 text-sm" data-testid="news-usage-error">{err}</div>;
  }
  if (!data) return null;

  const pct = data.credits.pct_used;
  const tone = pct >= 90 ? "#EF4444" : pct >= 70 ? "#F97316" : "#01AF9B";
  const maxSeries = Math.max(1, ...data.daily_series_14d.map(d => d.bulletins));
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const monthLabel = new Date(data.month_start).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="news-usage-panel">
      {/* Header + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-['Outfit'] text-lg font-bold text-white flex items-center gap-2">
            <Newspaper size={18} className="text-[#F97316]" weight="fill" />
            Tavily Credit Usage, ESG World News
          </h3>
          <p className="text-slate-500 text-xs mt-0.5">Each bulletin consumes {data.credits_per_bulletin} Tavily credits · Monthly window: {monthLabel}</p>
          <p className="text-slate-600 text-[11px] mt-1">Auto-alerts to <span className="text-slate-400">info@architaly.green</span> when usage crosses 70% / 90% / 100%.</p>
        </div>
        <button onClick={load} className="text-xs text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-colors" data-testid="news-usage-refresh">
          <ArrowsClockwise size={13} /> Refresh
        </button>
      </div>

      {/* Headline KPI, credits this month with progress bar */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-6" data-testid="news-usage-headline">
        <div className="flex items-end justify-between gap-4 mb-3">
          <div>
            <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">Credits this month</div>
            <div className="font-['Outfit'] text-4xl font-extrabold text-white">
              {data.credits.this_month.toLocaleString()}
              <span className="text-slate-500 text-xl font-bold ml-2">/ {data.monthly_limit.toLocaleString()}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-['Outfit'] text-2xl font-bold" style={{ color: tone }}>{pct.toFixed(1)}%</div>
            <div className="text-slate-500 text-[11px] uppercase tracking-wider">used</div>
          </div>
        </div>
        <div className="h-3 rounded-full bg-[#0B0C10] overflow-hidden border border-slate-800">
          <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: tone }} data-testid="news-usage-bar" />
        </div>
        <div className="flex justify-between mt-2 text-[11px] text-slate-500">
          <span>{data.credits.remaining_this_month.toLocaleString()} credits remaining</span>
          <span>Dev tier, 1,000 / month</span>
        </div>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="news-usage-counts">
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1 text-slate-400 text-[11px] uppercase tracking-wider font-semibold">
            <Lightning size={13} className="text-[#01AF9B]" weight="fill" /> Last 24h
          </div>
          <div className="font-['Outfit'] text-2xl font-bold text-white">{data.credits.last_24h}</div>
          <div className="text-slate-500 text-[11px]">{data.bulletins.last_24h} bulletins</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1 text-slate-400 text-[11px] uppercase tracking-wider font-semibold">
            <Calendar size={13} className="text-[#01AF9B]" /> Last 7 days
          </div>
          <div className="font-['Outfit'] text-2xl font-bold text-white">{data.credits.last_7_days}</div>
          <div className="text-slate-500 text-[11px]">{data.bulletins.last_7_days} bulletins</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1 text-slate-400 text-[11px] uppercase tracking-wider font-semibold">
            <Calendar size={13} className="text-[#01AF9B]" /> Last 30 days
          </div>
          <div className="font-['Outfit'] text-2xl font-bold text-white">{data.credits.last_30_days}</div>
          <div className="text-slate-500 text-[11px]">{data.bulletins.last_30_days} bulletins</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1 text-slate-400 text-[11px] uppercase tracking-wider font-semibold">
            <ChartBar size={13} className="text-[#01AF9B]" /> All time
          </div>
          <div className="font-['Outfit'] text-2xl font-bold text-white">{data.bulletins.total}</div>
          <div className="text-slate-500 text-[11px]">bulletins generated</div>
        </div>
      </div>

      {/* 14-day mini chart */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-5" data-testid="news-usage-chart">
        <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-4">Last 14 days · bulletins / day</div>
        <div className="flex items-end gap-1.5 h-28">
          {data.daily_series_14d.map((d, i) => {
            const h = (d.bulletins / maxSeries) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 group">
                <div className="text-[9px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">{d.bulletins}</div>
                <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(2, h)}%`, background: d.bulletins ? "#01AF9B" : "#1f2937", opacity: d.bulletins ? 0.85 : 1 }} title={`${d.date}: ${d.bulletins} bulletins (${d.credits} credits)`} />
                <div className="text-[9px] text-slate-600">{d.date.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top consumers this month */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-2xl overflow-hidden" data-testid="news-usage-top-users">
        <div className="px-5 py-4 border-b border-slate-800/60">
          <div className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Top consumers this month</div>
        </div>
        {data.top_users_this_month.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-500 text-sm">No bulletins generated this month yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0B0C10]">
              <tr className="text-slate-500 text-[10px] uppercase tracking-wider">
                <th className="text-left px-5 py-2 font-semibold">User</th>
                <th className="text-right px-5 py-2 font-semibold">Bulletins</th>
                <th className="text-right px-5 py-2 font-semibold">Credits</th>
                <th className="text-right px-5 py-2 font-semibold">Last used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40">
              {data.top_users_this_month.map((u, i) => (
                <tr key={u.user_id || i} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-white truncate max-w-[260px]">{u.email}</td>
                  <td className="px-5 py-3 text-right text-slate-300 font-mono">{u.bulletins}</td>
                  <td className="px-5 py-3 text-right text-[#01AF9B] font-mono font-semibold">{u.credits}</td>
                  <td className="px-5 py-3 text-right text-slate-500 text-xs">{fmtDate(u.last_used)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const RANGE_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "7d",    label: "Last 7d" },
  { id: "30d",   label: "Last 30d" },
  { id: "all",   label: "All time" },
];

const COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", AE: "United Arab Emirates", SA: "Saudi Arabia",
  IN: "India", DE: "Germany", FR: "France", IT: "Italy", ES: "Spain", NL: "Netherlands",
  CA: "Canada", AU: "Australia", SG: "Singapore", QA: "Qatar", KW: "Kuwait", BH: "Bahrain",
  OM: "Oman", EG: "Egypt", JO: "Jordan", LB: "Lebanon", PK: "Pakistan", BD: "Bangladesh",
  CN: "China", JP: "Japan", KR: "South Korea", BR: "Brazil", MX: "Mexico", ZA: "South Africa",
  TR: "Turkey", RU: "Russia", ID: "Indonesia", MY: "Malaysia", PH: "Philippines",
  "??": "Unknown",
};

const TrafficStatsPanel = () => {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setErr("");
    api.adminTrafficStats(range)
      .then(setData)
      .catch(e => setErr(e?.response?.data?.detail || e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [range]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="traffic-loading">
        <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-6" data-testid="traffic-error">
        {err}
      </div>
    );
  }

  const conv = (num, denom) => (denom > 0 ? Math.round((num / denom) * 1000) / 10 : 0);
  const f = data.funnel || {};
  const visits    = f.visits           || 0;
  const signups   = f.signups          || 0;
  const subs      = f.subscribers      || 0;
  const generated = f.reports_generated|| 0;

  return (
    <div data-testid="traffic-panel">
      {/* Range selector */}
      <div className="flex items-center gap-2 mb-6" data-testid="traffic-range">
        {RANGE_OPTIONS.map(o => (
          <button key={o.id} onClick={() => setRange(o.id)} data-testid={`range-${o.id}`}
            className={`px-4 py-2 rounded-lg font-['Outfit'] text-sm font-semibold transition-all ${
              range === o.id
                ? "bg-[#01AF9B] text-white"
                : "bg-[#111216] border border-slate-800/60 text-slate-400 hover:text-white"
            }`}>
            {o.label}
          </button>
        ))}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-pageviews">
          <div className="flex items-center gap-3 mb-2"><ChartLine size={20} className="text-[#01AF9B]" /><span className="text-slate-400 text-sm">Page views</span></div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">{(data.total_pageviews || 0).toLocaleString()}</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-visitors">
          <div className="flex items-center gap-3 mb-2"><Users size={20} className="text-[#B94B05]" /><span className="text-slate-400 text-sm">Unique visitors</span></div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">{(data.unique_visitors || 0).toLocaleString()}</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-sessions">
          <div className="flex items-center gap-3 mb-2"><Globe size={20} className="text-[#1E3A5F]" /><span className="text-slate-400 text-sm">Sessions</span></div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">{(data.unique_sessions || 0).toLocaleString()}</div>
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="kpi-vps">
          <div className="flex items-center gap-3 mb-2"><ChartBar size={20} className="text-[#73324B]" /><span className="text-slate-400 text-sm">Views / session</span></div>
          <div className="font-['Outfit'] text-3xl font-bold text-white">
            {data.unique_sessions > 0
              ? (data.total_pageviews / data.unique_sessions).toFixed(1)
              : "0.0"}
          </div>
        </div>
      </div>

      {/* Timeseries chart */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5 mb-8" data-testid="traffic-chart">
        <div className="font-['Outfit'] text-sm font-semibold text-slate-300 mb-4">Views &amp; sessions over time</div>
        {(data.timeseries || []).length === 0 ? (
          <div className="text-slate-500 text-sm py-12 text-center">No data for this range yet.</div>
        ) : (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.timeseries}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0b0c10", border: "1px solid #334155", borderRadius: 8 }} labelStyle={{ color: "#e2e8f0" }} />
                <Line type="monotone" dataKey="views" stroke="#01AF9B" strokeWidth={2} dot={false} name="Page views" />
                <Line type="monotone" dataKey="sessions" stroke="#B94B05" strokeWidth={2} dot={false} name="Sessions" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Conversion funnel */}
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5 mb-8" data-testid="funnel-panel">
        <div className="font-['Outfit'] text-sm font-semibold text-slate-300 mb-4">Conversion funnel</div>
        <div className="space-y-3">
          {[
            { label: "Visitors",         value: visits,    pct: 100 },
            { label: "Signups",          value: signups,   pct: conv(signups, visits) },
            { label: "Subscribers",      value: subs,      pct: conv(subs, visits) },
            { label: "Reports generated",value: generated, pct: conv(generated, visits) },
          ].map((step, i) => (
            <div key={step.label} className="flex items-center gap-4" data-testid={`funnel-step-${i}`}>
              <div className="w-40 text-slate-400 text-sm">{step.label}</div>
              <div className="flex-1 h-8 bg-[#0b0c10] rounded-lg overflow-hidden relative">
                <div className="h-full rounded-lg transition-all" style={{
                  width: `${Math.max(step.pct, 2)}%`,
                  background: i === 0 ? "#01AF9B" : i === 1 ? "#B94B05" : i === 2 ? "#1E3A5F" : "#73324B",
                }} />
                <div className="absolute inset-0 flex items-center px-3 text-white font-['Outfit'] text-xs font-semibold">
                  {step.value.toLocaleString()} <span className="ml-2 text-white/60">({step.pct}%)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top pages / referrers / countries */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopList title="Top pages" icon={FileText} iconColor="#01AF9B"
          rows={data.top_pages || []} testid="top-pages" formatKey={k => k} />
        <TopList title="Top referrers" icon={LinkIcon} iconColor="#B94B05"
          rows={data.top_referrers || []} testid="top-referrers" formatKey={k => k} />
        <TopList title="Top countries" icon={Globe} iconColor="#1E3A5F"
          rows={data.top_countries || []} testid="top-countries"
          formatKey={k => `${k}, ${COUNTRY_NAMES[k] || "-"}`} />
      </div>

      {/* ── Country breakdown: free vs paid users per country ── */}
      <div className="mt-8">
        <CountryBreakdownPanel />
      </div>
    </div>
  );
};

// ─── Country breakdown (Feb 2026 — LinkedIn ad-campaign visibility) ──
// Segments the whole registered user base by country and splits each
// row into free vs paid. Useful to see which countries convert best
// after paid signups arrive (informs ad-targeting decisions).
const CountryBreakdownPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setErr("");
    api.adminCountryBreakdown()
      .then(setData)
      .catch(e => setErr(e?.response?.data?.detail || e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="country-breakdown-loading">
        <div className="h-5 w-40 bg-slate-800 rounded animate-pulse mb-4" />
        <div className="h-32 bg-slate-800/50 rounded animate-pulse" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-6" data-testid="country-breakdown-error">
        {err}
      </div>
    );
  }

  const rows = data?.by_country || [];
  const totalFree = data?.total_free || 0;
  const totalPaid = data?.total_paid || 0;
  const overallConv = data?.conversion_rate_pct || 0;

  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid="country-breakdown-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Globe size={18} className="text-[#01AF9B]" />
          <span className="font-['Outfit'] text-sm font-semibold text-slate-300">
            Users by country, free vs paid
          </span>
        </div>
        <div className="text-xs text-slate-400 font-['Outfit']">
          Free: <span className="text-white font-semibold">{totalFree}</span>
          <span className="mx-2 text-slate-600">/</span>
          Paid: <span className="text-white font-semibold">{totalPaid}</span>
          <span className="mx-2 text-slate-600">/</span>
          Overall conversion: <span className="text-[#01AF9B] font-semibold">{overallConv}%</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-slate-500 text-sm py-6 text-center">
          No user data yet. Country attribution starts filling as new users sign up.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 border-b border-slate-800/60 text-xs uppercase tracking-wider">
                <th className="px-3 py-2 text-left font-semibold">Country</th>
                <th className="px-3 py-2 text-right font-semibold">Free users</th>
                <th className="px-3 py-2 text-right font-semibold">% of all free</th>
                <th className="px-3 py-2 text-right font-semibold">Paid users</th>
                <th className="px-3 py-2 text-right font-semibold">% of all paid</th>
                <th className="px-3 py-2 text-right font-semibold">Country conv.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.country || i}
                    className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors"
                    data-testid={`country-row-${r.country || i}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-block px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-xs font-mono">
                      {r.country || "??"}
                    </span>
                    <span className="ml-2 text-slate-300">{COUNTRY_NAMES[r.country] || "-"}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-200 font-['Outfit']">{r.free}</td>
                  <td className="px-3 py-2.5 text-right text-slate-400 text-xs">{r.free_pct_of_free}%</td>
                  <td className="px-3 py-2.5 text-right text-[#01AF9B] font-['Outfit'] font-semibold">{r.paid}</td>
                  <td className="px-3 py-2.5 text-right text-slate-400 text-xs">{r.paid_pct_of_paid}%</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`text-xs font-semibold ${r.conversion_rate_pct >= 10 ? "text-[#01AF9B]" : r.conversion_rate_pct > 0 ? "text-amber-400" : "text-slate-500"}`}>
                      {r.conversion_rate_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-500 font-['Outfit'] leading-relaxed">
        Country is captured at signup from the CDN edge (Cloudflare) with an ip-api.com fallback.
        Older users backfill automatically the next time they hit <span className="font-mono">/api/auth/me</span>.
      </p>
    </div>
  );
};

const TopList = ({ title, icon: Icon, iconColor, rows, testid, formatKey }) => (
  <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} style={{ color: iconColor }} />
      <span className="font-['Outfit'] text-sm font-semibold text-slate-300">{title}</span>
    </div>
    {rows.length === 0 ? (
      <div className="text-slate-500 text-sm py-4">No data yet.</div>
    ) : (
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm" data-testid={`${testid}-row-${i}`}>
            <span className="text-slate-300 truncate pr-3">{formatKey(r.key)}</span>
            <span className="font-['Outfit'] text-white font-semibold">{r.count.toLocaleString()}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── Advisors Admin Panel ───────────────────────────────────────────
const AdvisorsAdminPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [acting, setActing] = useState(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const API_BASE = `${BACKEND}/api`;
  const token = typeof window !== "undefined" ? localStorage.getItem("esg_token") : "";
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}/admin/advisors${filter !== "all" ? `?status=${filter}` : ""}`;
      const r = await fetch(url, { headers: authHeaders, credentials: "include" });
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setActing(id);
    try {
      const r = await fetch(`${API_BASE}/admin/advisors/${id}/approve`, {
        method: "POST", headers: authHeaders, credentials: "include",
      });
      if (!r.ok) throw new Error("approve failed");
      await load();
    } catch (e) { alert("Failed to approve: " + e.message); }
    finally { setActing(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Why is this profile being rejected? (shown to the advisor)");
    if (!reason || !reason.trim()) return;
    setActing(id);
    try {
      const r = await fetch(`${API_BASE}/admin/advisors/${id}/reject`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!r.ok) throw new Error("reject failed");
      await load();
    } catch (e) { alert("Failed to reject: " + e.message); }
    finally { setActing(null); }
  };

  const statusChip = (s) => {
    const map = {
      pending:  { bg: "rgba(245,158,11,0.12)", color: "#F59E0B", label: "Pending" },
      approved: { bg: "rgba(1,175,155,0.12)",  color: "#01AF9B", label: "Approved" },
      rejected: { bg: "rgba(239,68,68,0.12)",  color: "#EF4444", label: "Rejected" },
    }[s] || { bg: "rgba(148,163,184,0.12)", color: "#94A3B8", label: s };
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: map.bg, color: map.color }}>
        {map.label}
      </span>
    );
  };

  const pendingCount = items.filter(i => i.status === "pending").length;

  return (
    <div data-testid="advisors-admin-panel">
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="advisors-admin-filter-bar">
        {[
          { id: "pending",  label: `Pending${filter === "pending" ? ` (${pendingCount})` : ""}` },
          { id: "approved", label: "Approved" },
          { id: "rejected", label: "Rejected" },
          { id: "all",      label: "All" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            data-testid={`advisors-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === f.id ? "bg-[#01AF9B] text-black" : "bg-white/5 text-slate-400 hover:text-white"
            }`}>{f.label}</button>
        ))}
        <button onClick={load} data-testid="advisors-admin-refresh"
          className="ms-auto flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="advisors-admin-loading">
          <div className="w-6 h-6 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-10 text-center text-slate-500 text-sm" data-testid="advisors-admin-empty">
          No professional profiles match this filter.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="advisors-admin-list">
          {items.map(a => (
            <div key={a.id} className="bg-[#111216] border border-slate-800/60 rounded-xl p-5 flex flex-col sm:flex-row gap-5"
                 data-testid={`advisors-admin-row-${a.slug}`}>
              <div className="w-20 h-20 rounded-full overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
                {a.image_url ? (
                  <img src={`${BACKEND}${a.image_url}`} alt={a.company_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-2xl">{a.company_name?.[0] || "?"}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h3 className="font-['Outfit'] text-lg font-bold text-white truncate">{a.company_name}</h3>
                  {statusChip(a.status)}
                </div>
                <p className="text-slate-400 text-sm mb-2">{a.first_name} {a.last_name}</p>
                <p className="text-slate-300 text-sm italic mb-3 line-clamp-2">"{a.hero_message}"</p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400 mb-3">
                  <div className="flex items-center gap-1.5 truncate"><Envelope size={12} /> {a.email}</div>
                  <div className="flex items-center gap-1.5"><Phone size={12} /> {a.phone}</div>
                  <div className="flex items-center gap-1.5"><MapPin size={12} /> {a.city}, {a.country}</div>
                  <div className="flex items-center gap-1.5"><Clock size={12} /> {a.created_at?.slice(0, 10)}</div>
                </div>
                {(a.service_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {a.service_tags.slice(0, 6).map(t => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400">{t}</span>
                    ))}
                  </div>
                )}
                {a.status === "rejected" && a.rejection_reason && (
                  <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-md p-2 mb-3">
                    Previous rejection reason: {a.rejection_reason}
                  </div>
                )}
                <p className="text-slate-500 text-xs line-clamp-3">{a.bio}</p>
              </div>
              <div className="flex sm:flex-col gap-2 flex-shrink-0 justify-end">
                <button onClick={() => window.open(`/professionals/${a.slug}`, "_blank")}
                  data-testid={`advisors-admin-view-${a.slug}`}
                  className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <Eye size={12} /> View
                </button>
                {a.status !== "approved" && (
                  <button onClick={() => approve(a.id)} disabled={acting === a.id}
                    data-testid={`advisors-admin-approve-${a.slug}`}
                    className="px-3 py-1.5 rounded-lg bg-[#01AF9B] hover:bg-[#018F7E] text-black text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    <CheckCircle size={12} weight="bold" /> Approve
                  </button>
                )}
                {a.status !== "rejected" && (
                  <button onClick={() => reject(a.id)} disabled={acting === a.id}
                    data-testid={`advisors-admin-reject-${a.slug}`}
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    <XCircle size={12} weight="bold" /> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("health");  // start on health tab so admin sees live status first

  useEffect(() => {
    if (!user?.is_admin) { navigate("/dashboard"); return; }
    Promise.all([api.adminUsers(), api.adminStats()])
      .then(([u, s]) => { setUsers(u); setStats(s); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <div className="min-h-screen" style={{ background: "#070709" }}>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070709]/70 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo size="md" /></div>
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="back-dashboard">
            <ArrowLeft size={18} /> Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="font-['Outfit'] text-xs font-semibold tracking-[0.2em] uppercase text-[#73324B] mb-3 flex items-center gap-2">
            <span className="w-6 h-[2px] bg-[#73324B] inline-block"></span> Administration
          </div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-slate-400 text-sm">Manage users & monitor generation health</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-slate-800/60" data-testid="admin-tabs">
          {[
            { id: "health", label: "Generation Health", icon: Heartbeat },
            { id: "traffic", label: "Traffic Stats", icon: ChartLine },
            { id: "samples", label: "Sample Downloads", icon: DownloadSimple },
            { id: "subscribers", label: "Subscribers", icon: CurrencyDollar },
            { id: "news", label: "News Usage", icon: Newspaper },
            { id: "advisors", label: "ESG Professionals", icon: IdentificationCard },
            { id: "users", label: "Users", icon: Users },
            { id: "security", label: "Security", icon: ShieldCheck },
          ].map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-['Outfit'] text-sm font-semibold border-b-2 -mb-px transition-all ${active ? "text-[#01AF9B] border-[#01AF9B]" : "text-slate-500 border-transparent hover:text-slate-300"}`}>
                <Icon size={16} weight={active ? "fill" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Top stats (always visible) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-stats">
          <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2"><Users size={20} className="text-[#01AF9B]" /><span className="text-slate-400 text-sm">Total Users</span></div>
            <div className="font-['Outfit'] text-3xl font-bold text-white">{stats.total_users || 0}</div>
          </div>
          <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2"><FileText size={20} className="text-[#B94B05]" /><span className="text-slate-400 text-sm">Total Reports</span></div>
            <div className="font-['Outfit'] text-3xl font-bold text-white">{stats.total_reports || 0}</div>
          </div>
          <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2"><ChartBar size={20} className="text-[#1E3A5F]" /><span className="text-slate-400 text-sm">Generated Reports</span></div>
            <div className="font-['Outfit'] text-3xl font-bold text-white">{stats.generated_reports || 0}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" /></div>
        ) : tab === "health" ? (
          <GenerationHealthPanel />
        ) : tab === "traffic" ? (
          <TrafficStatsPanel />
        ) : tab === "samples" ? (
          <SampleDownloadsPanel />
        ) : tab === "subscribers" ? (
          <SubscribersPanel stats={stats} />
        ) : tab === "news" ? (
          <NewsUsagePanel />
        ) : tab === "advisors" ? (
          <AdvisorsAdminPanel />
        ) : tab === "security" ? (
          <SecurityPanel />
        ) : (
          <UsersPanel users={users} setUsers={setUsers} setStats={setStats} />
        )}
      </main>
    </div>
  );
}
